import { createGlobalStyle } from 'styled-components';
import fonts from './fonts';
import variables from './variables';
import TransitionStyles from './TransitionStyles';
import PrismStyles from './PrismStyles';

const GlobalStyle = createGlobalStyle`
  ${fonts};
  ${variables};

  /* Registered so it can be animated — a plain custom property would jump
     between keyframes instead of interpolating. Drives the rotating border
     highlight on the collapsed chat button. */
  @property --glow-angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
  }

  html {
    box-sizing: border-box;
    width: 100%;
    scroll-behavior: smooth;
  }

  *,
  *:before,
  *:after {
    box-sizing: inherit;
  }

  ::selection {
    background-color: var(--border);
    color: var(--text-emphasis);
  }

  /* Provide basic, default focus styles.*/
  :focus {
    outline: 2px solid var(--accent);
    outline-offset: 3px;
  }

  /*
    Remove default focus styles for mouse users ONLY if
    :focus-visible is supported on this platform.
  */
  :focus:not(:focus-visible) {
    outline: none;
    outline-offset: 0px;
  }

  /*
    Optionally: If :focus-visible is supported on this
    platform, provide enhanced focus styles for keyboard
    focus.
  */
  :focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 3px;
  }

  /* Scrollbar Styles */
  html {
    scrollbar-width: thin;
    scrollbar-color: var(--text-disabled) var(--bg);
  }
  ::-webkit-scrollbar {
    width: 12px;
  }
  ::-webkit-scrollbar-track {
    background: var(--bg);
  }
  ::-webkit-scrollbar-thumb {
    background-color: var(--text-disabled);
    border: 3px solid var(--bg);
    border-radius: 10px;
  }

  body {
    margin: 0;
    width: 100%;
    min-height: 100%;
    overflow-x: hidden;
    /* Per theme. Greyscale antialiasing thins glyphs, which suits light text
       on a dark page (it blooms and needs taking back) and works against dark
       text on a light one, where the same setting leaves the type visibly
       lighter than the dark theme's at the same weight. */
    -moz-osx-font-smoothing: var(--font-smoothing-moz);
    -webkit-font-smoothing: var(--font-smoothing);
    background-color: var(--bg);
    color: var(--text-body);
    font-family: var(--font-sans);
    /* The prose default for the whole site. Anything that wants to differ
       says so; anything that says nothing is body copy and gets this. It used
       to be --fz-xl here and --fz-lg under 480px, which meant the same
       paragraph was two different sizes on two devices. */
    font-size: var(--fz-lg);
    line-height: var(--lh-body);

    &.hidden {
      overflow: hidden;
    }

    &.blur {
      overflow: hidden;

      /* The header deliberately keeps its own background here. Clearing it —
         as this used to — removed the 65%-opaque overlay the scrolled state
         paints, leaving only the content at brightness(0.7) showing through.
         That is much lighter than the overlay it replaced, so opening the menu
         made the strip behind the navbar jump brighter while the rest of the
         page dimmed. */

      .page-content > * {
        filter: blur(5px) brightness(0.7);
        transition: var(--transition);
        pointer-events: none;
        user-select: none;
      }
    }
  }

  #root {
    min-height: 100vh;
    display: grid;
    grid-template-rows: 1fr auto;
    grid-template-columns: 100%;
  }

  /* Top padding is measured from the bottom of the fixed header, not from the
     top of the page: the header is --nav-scroll-height tall and sits over the
     content. At the smaller breakpoints the flat value was less than the header
     itself, so the first thing on the page — usually the "← Back" link — was
     starting underneath it. */
  main {
    margin: 0 auto;
    width: 100%;
    max-width: var(--content-width);
    min-height: 100vh;
    padding: calc(var(--nav-scroll-height) + var(--space-8)) var(--gutter) var(--space-10);

    @media (max-width: 1080px) {
      padding: calc(var(--nav-scroll-height) + var(--space-8)) var(--gutter-md) var(--space-10);
    }
    @media (max-width: 768px) {
      padding: calc(var(--nav-scroll-height) + var(--space-6)) var(--gutter-sm) var(--space-9);
    }
    @media (max-width: 480px) {
      padding: calc(var(--nav-scroll-height) + var(--space-5)) var(--gutter-xs) var(--space-8);
    }

    &.fillHeight {
      padding: 0 var(--gutter);

      @media (max-width: 1080px) {
        padding: 0 var(--gutter-md);
      }
      @media (max-width: 768px) {
        padding: 0 var(--gutter-sm);
      }
      @media (max-width: 480px) {
        padding: 0 var(--gutter-xs);
      }
    }
  }

  section {
    margin: 0 auto;
    padding: var(--space-9) 0;
    max-width: 1000px;
    /* Keeps an anchored section clear of the fixed header. */
    scroll-margin-top: var(--nav-height);

    @media (max-width: 768px) {
      padding: var(--space-8) 0;
    }

    @media (max-width: 480px) {
      padding: var(--space-7) 0;
    }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0 0 var(--space-3) 0;
    font-weight: 600;
    color: var(--text-emphasis);
    line-height: var(--lh-title);
  }

  .big-heading {
    margin: 0;
    font-size: clamp(40px, 8vw, 80px);
    line-height: var(--lh-display);
  }

  .medium-heading {
    margin: 0;
    font-size: clamp(40px, 8vw, 60px);
  }

  .small-heading {
    margin: 0;
    font-size: clamp(28px, 4vw, var(--fz-heading));
  }

  /* Section headings keep the numeral and the trailing rule — without them a
     conversational title ("I Write about AI, Philosophy, and More") doesn't
     read as a section boundary. Numeral is neutral and set in the sans. */
  .numbered-heading {
    display: flex;
    /* Baseline, not centre: the numeral is a different size from the title,
       so centring their line boxes leaves their baselines a few px apart.
       Baseline alignment sits them on the same line at any clamped size. */
    align-items: baseline;
    position: relative;
    margin: 0 0 var(--space-7);
    width: 100%;
    color: var(--text);
    font-size: clamp(28px, 4vw, var(--fz-heading));
    font-weight: 600;
    letter-spacing: -0.02em;
    line-height: var(--lh-heading);
    white-space: nowrap;

    /* nowrap works while the titles are short, and clips silently once they
       are not: at 375px the blogs heading was already losing its last words,
       and a longer one loses a third of itself. Below this width let the title
       wrap and drop the trailing rule — the heading spans the whole column
       here, so the rule was only ever filling space that no longer exists.
       Wrapped lines hang under the first, clear of the numeral, because the
       title is a single flex item that starts after it. */
    @media (max-width: 768px) {
      white-space: normal;
    }

    &:before {
      position: relative;
      counter-increment: section;
      content: '0' counter(section) '.';
      margin-right: var(--space-3);
      color: var(--accent);
      font-family: var(--font-sans);
      font-size: clamp(var(--fz-xxl), 3.5vw, 30px);
      font-weight: 400;
      letter-spacing: 0;

      @media (max-width: 480px) {
        margin-right: var(--space-2);
      }
    }

    &:after {
      content: '';
      display: block;
      position: relative;
      /* Opts out of the baseline alignment above and sits on the title's
         optical midline instead. Offset measured against the cap-height band. */
      align-self: baseline;
      top: -0.32em;
      width: 300px;
      height: 1px;
      margin-left: var(--space-5);
      background-color: var(--border);

      @media (max-width: 1080px) {
        width: 200px;
      }
      /* Gone once the title is allowed to wrap. The rule's job is to close off
         the space a short title leaves to its right; a title that now fills
         two or three full-width lines leaves none, so all the rule can do is
         steal width from the last line and force another wrap. */
      @media (max-width: 768px) {
        display: none;
      }
      @media (max-width: 600px) {
        margin-left: var(--space-3);
      }
    }
  }

  /* Entrance animation, driven by hooks/useReveal. The attribute is only ever
     set by that hook, so an element that JavaScript never reaches keeps its
     normal appearance instead of being stuck at opacity 0. */
  [data-reveal] {
    transition: opacity 0.5s var(--easing), transform 0.5s var(--easing);
  }

  [data-reveal='hidden'] {
    opacity: 0;
    transform: translateY(20px);
  }

  @media (prefers-reduced-motion: reduce) {
    [data-reveal] {
      opacity: 1;
      transform: none;
      transition: none;
    }
  }

  img,
  svg,
  .gatsby-image-wrapper {
    width: 100%;
    max-width: 100%;
    vertical-align: middle;
  }

  img[alt=""],
  img:not([alt]) {
    filter: blur(5px);
  }

  svg {
    width: 100%;
    height: 100%;
    fill: currentColor;
    vertical-align: middle;

    &.feather {
      fill: none;
    }
  }

  a {
    display: inline-block;
    text-decoration: none;
    text-decoration-skip-ink: auto;
    color: inherit;
    position: relative;
    transition: var(--transition);

    /* focus-visible, not focus: a mouse click leaves focus on the link, so
       with plain :focus the hover colour stayed lit until you clicked
       somewhere else. Most visible on links that don't navigate away — an
       external link, or a tag whose filter is already applied. */
    &:hover,
    &:focus-visible {
      color: var(--accent);
    }

    &.inline-link {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  button {
    cursor: pointer;
    border: 0;
    border-radius: 0;
  }

  input, textarea {
    border-radius: 0;
    outline: 0;

    &:focus {
      outline: 0;
    }
    &:focus,
    &:active {
      &::placeholder {
        opacity: 0.5;
      }
    }
  }

  p {
    margin: 0 0 var(--space-4) 0;

    &:last-child,
    &:last-of-type {
      margin: 0;
    }

    & > a {
      ${({ theme }) => theme.mixins.inlineLink};
    }

    & > code {
      background-color: var(--surface);
      color: var(--text);
      font-size: var(--fz-sm);
      border-radius: var(--border-radius);
      padding: 0.3em 0.5em;
    }
  }

  ul.fancy-list {
    ${({ theme }) => theme.mixins.fancyList};
  }

  blockquote {
    border-left-color: var(--border-strong);
    border-left-style: solid;
    border-left-width: 1px;
    margin-left: 0px;
    margin-right: 0px;
    padding-left: 1.5rem;

    p {
      font-style: italic;
      font-size: var(--fz-xxl);
    }
  }

  hr {
    background-color: var(--border);
    height: 1px;
    border-width: 0px;
    border-style: initial;
    border-color: initial;
    border-image: initial;
    margin: 1rem;
  }

  code {
    font-family: var(--font-sans);
    font-size: var(--fz-md);
  }

  .overline {
    color: var(--text-muted);
    font-family: var(--font-sans);
    font-size: var(--fz-md);
    font-weight: 400;
  }

  /* Lifts a phrase out of body copy without pretending to be a link.
     Used where the markup previously had a bare <a> with no href. */
     
  .emphasis {
    color: var(--text-emphasis);
    font-style: normal;
    font-weight: 500;
  }

  .subtitle {
    color: var(--text-secondary);
    margin: 0 0 var(--space-5) 0;
    font-size: var(--fz-md);
    font-family: var(--font-sans);
    font-weight: 400;
    line-height: var(--lh-body);
    @media (max-width: 1080px) {
      font-size: var(--fz-md);
    }
    @media (max-width: 768px) {
      font-size: var(--fz-md);
    }

    a {
      ${({ theme }) => theme.mixins.inlineLink};
      line-height: var(--lh-body);
    }
  }

  .breadcrumb {
    display: flex;
    align-items: center;
    margin-bottom: var(--space-5);
    color: var(--text-muted);

    .arrow {
      display: block;
      padding-top: 4px;
    }

    a {
      ${({ theme }) => theme.mixins.inlineLink};
      font-family: var(--font-sans);
      font-size: var(--fz-sm);
      font-weight: 600;
      line-height: var(--lh-body);
      text-transform: uppercase;
      letter-spacing: 0.1em;
    }
  }

  .gatsby-image-outer-wrapper {
    height: 100%;
  }

  ${TransitionStyles};

  ${PrismStyles};
`;

export default GlobalStyle;
